// =============================================================================
// Invoiceexport.js
// Brand resolver, print (isolated iframe), PDF, share-link messages.
// Install once:  npm i html2canvas jspdf qrcode.react
// =============================================================================

import { API_ROOT } from '../../../config/api';
const MM_TO_PX = 96 / 25.4;
const A4_W = 210;
const A4_H = 297;
const PAGE_MARGIN_MM = 8;

export const INVOICE_FONT = 'Nunito';
export const INVOICE_FONT_CSS =
  'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800;900&display=swap';

// Backend address comes from the one shared place (src/config/api.js): own domain, same-origin, or live
const onOwnComputer = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
export const API_ORIGIN = API_ROOT;

/** Invoice link for the client. The server's link is kept (it gives the rich WhatsApp preview).
 *  Only a localhost link on the live site is repaired to this app's address + /i/<token>. */
export const toAppInvoiceLink = (url) => {
  const link = String(url || '');
  const m = link.match(/^https?:\/\/([^/:]+)[^/]*\/i\/([A-Za-z0-9_-]+)\/?(?:[?#].*)?$/);
  if (!m || typeof window === 'undefined') return link;
  const linkIsLocal = /^(localhost|127\.0\.0\.1)$/.test(m[1]);
  return linkIsLocal && !onOwnComputer ? `${window.location.origin}/i/${m[2]}` : link;
};

/* =============================================================================
   BRAND / SETTINGS RESOLVER
   One place that reads salon name, address, logo, GSTIN from any settings shape.
   ============================================================================= */
const firstText = (...values) => {
  for (const v of values) {
    if (typeof v === 'string' || typeof v === 'number') {
      const s = String(v).trim();
      if (s) return s;
    }
  }
  return '';
};

const addressToText = (a) => {
  if (!a) return '';
  if (typeof a === 'string') return a.trim();
  if (typeof a !== 'object') return '';
  return [
    a.line1, a.addressLine1, a.street, a.line2, a.addressLine2, a.area, a.landmark,
    a.city, a.district, a.state, a.pincode, a.pinCode, a.zip, a.postalCode,
  ]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(', ');
};

const appendMissing = (base, parts) =>
  parts.reduce((out, part) => {
    const s = String(part || '').trim();
    if (!s || out.toLowerCase().includes(s.toLowerCase())) return out;
    return out ? `${out}, ${s}` : s;
  }, base || '');

export const resolveAssetUrl = (value) => {
  const raw = value && typeof value === 'object' ? value.url || value.secure_url || value.src || value.path : value;
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^(data:|blob:|https?:\/\/)/i.test(s)) return s;
  if (s.startsWith('//')) return `https:${s}`;
  return `${API_ORIGIN}/${s.replace(/^\/+/, '')}`; // "/uploads/logo.png" -> full URL
};

// settings = store settings API, invoice = invoice doc, store = activeStore / store doc (fallback)
export function getInvoiceBrand(settings = {}, invoice = {}, store = {}) {
  const s = settings || {};
  const profile = s.profile || {};
  const tax = s.tax || {};
  const invoicing = s.invoicing || {};
  const payment = s.payment || {};
  const st = store && !store.isHQ ? store : {};

  const salonName =
    firstText(
      invoicing.invoiceSalonName, profile.businessName, profile.salonName, st.storeName,
      profile.storeName, st.name, profile.name, tax.tradeName, tax.legalName
    ) || 'Salon';

  let address = firstText(invoicing.invoiceAddress) || addressToText(profile.address);
  address = appendMissing(address, [
    profile.addressLine1, profile.addressLine2, profile.area,
    profile.city, profile.state, profile.pincode || profile.pinCode,
  ]);
  if (!address) address = addressToText(tax.address) || firstText(tax.principalAddress);
  if (!address) {
    address = appendMissing(addressToText(st.address), [st.city, st.state, st.pincode || st.pinCode]);
  }

  // GSTIN: invoice snapshot first (what was billed), then live settings, then store
  const gstin = firstText(
    invoice?.pricing?.gstin, invoice?.summary?.gstin, s.invoiceGstin,
    tax.gstin, profile.gstin, st.gstin, st.gstNumber, st.gstNo
  ).replace(/\s+/g, '').toUpperCase();
  const isGstBill = Boolean(invoice?.pricing?.isGstBill ?? invoice?.summary?.isGstBill ?? false);

  return {
    salonName,
    storeName: firstText(st.storeName, st.name),
    legalName: firstText(tax.legalName, profile.legalName),
    tagline: firstText(invoicing.invoiceTagline),
    address,
    phone: firstText(
      profile.phone, profile.contactNumber, profile.mobile, profile.phoneNumber, invoicing.phone,
      st.phone, st.contactNumber, st.mobile
    ),
    email: firstText(profile.email, invoicing.email, st.email),
    contactLine: firstText(invoicing.invoiceContactLine),
    // Uploaded logo (Settings > Business profile) first, then older logo fields
    logoUrl: resolveAssetUrl(
      s.branding?.logo || invoicing.logo || profile.logo || profile.logoUrl || st.logo || st.logoUrl
    ),
    gstin,
    isGstBill: isGstBill && Boolean(gstin),
    upiId: firstText(invoicing.upiId, payment.upiId, profile.upiId),
    bankDetails: firstText(invoicing.bankDetails, payment.bankDetails),
    terms: firstText(invoicing.terms),
    footerNote: firstText(invoicing.footerNote),
  };
}

/* ---------- Formatting ---------- */
export const formatMoneyINR = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/* Invoice lines straight from the saved invoice (backend already calculated GST) */
export const getInvoiceLines = (invoice = {}) =>
  (invoice.items || [])
    .filter((it) => String(it?.itemName || '').trim())
    .map((it) => {
      const qty = num(it.qty);
      const rate = num(it.rate);
      const gstRate = num(it.gstRate ?? it.taxPercent);
      const gstAmount = num(it.gstAmount);
      const taxableAmount = num(it.taxableAmount, rate * qty);
      return {
        itemName: String(it.itemName).trim(),
        qty,
        enteredRate: rate,
        taxableRate: num(it.taxableRate, rate),
        taxableAmount,
        gstRate,
        gstAmount,
        discountAmount: num(it.discountAmount),
        amount: num(it.amount, taxableAmount + gstAmount),
      };
    });

const PAYMENT_LABELS = {
  cash: 'Cash',
  card: 'Card',
  upi: 'UPI',
  wallet: 'Khata wallet',
  prepaidCardAmount: 'Prepaid card',
};

export function getPaymentInfo(invoice = {}) {
  const p = invoice.payments || {};
  const modes = Object.keys(PAYMENT_LABELS)
    .filter((k) => Number(p[k]) > 0)
    .map((k) => ({ key: k, label: PAYMENT_LABELS[k], amount: Number(p[k]) }));
  const totalPaid = Number(invoice.paymentTracking?.totalPaid ?? invoice.summary?.totalPaid ?? modes.reduce((s, m) => s + m.amount, 0));
  const grand = Number(invoice.summary?.grandTotal || 0);
  const balanceDue = Number(invoice.paymentTracking?.balanceDue ?? invoice.summary?.balanceDue ?? grand - totalPaid);
  const status = invoice.paymentTracking?.paymentStatus || invoice.summary?.paymentStatus || 'pending';
  return { modes, totalPaid, balanceDue, status };
}

/* ---------- Paper helpers ---------- */
export const getPaperWidthMm = (template) => {
  if (!template?.isThermal) return A4_W;
  return template.paperClass === 'paperThermal58' ? 58 : 80;
};

// Must match widths in InvoiceDocument.module.css
export const getPaperWidthPx = (template) => {
  if (!template?.isThermal) return 794;
  return template.paperClass === 'paperThermal58' ? 219 : 302;
};

/* ---------- Small helpers ---------- */
export const sanitizeFileName = (value) =>
  String(value || 'invoice')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

export const normalizePhone = (value, countryCode = '91') => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 10) return `${countryCode}${digits}`;
  if (digits.length === 11 && digits.startsWith('0')) return `${countryCode}${digits.slice(1)}`;
  return digits;
};

export const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

export const buildUpiUrl = ({ upiId, name, amount, note }) =>
  `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(name || '')}` +
  `&am=${Number(amount || 0).toFixed(2)}&cu=INR&tn=${encodeURIComponent(note || '')}`;

export const formatINR = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const escapeHtml = (s) =>
  String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const isMobileDevice = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px;top:0;';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  }
}

/* ---------- Fonts ---------- */
export async function ensureInvoiceFonts(doc = document) {
  if (!doc.fonts?.load) return;
  try {
    await Promise.all(
      ['400', '500', '600', '700', '800', '900'].map((w) => doc.fonts.load(`${w} 14px "${INVOICE_FONT}"`))
    );
    await doc.fonts.ready;
  } catch {
    /* system font fallback */
  }
}

/* ---------- Images -> data URLs (so html2canvas never drops the logo) ---------- */
const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

async function inlineImages(root) {
  const imgs = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    imgs.map(async (img) => {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:')) return;
      try {
        const res = await fetch(src, { mode: 'cors', cache: 'force-cache' });
        if (!res.ok) throw new Error('image fetch failed');
        img.src = await blobToDataUrl(await res.blob());
        if (img.decode) await img.decode().catch(() => {});
      } catch {
        /* keep src; html2canvas useCORS will retry */
      }
    })
  );
}

/* ---------- Amount in words (Indian numbering) ---------- */
const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven',
  'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const twoDigits = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ''}`);
const threeDigits = (n) => {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return [h ? `${ONES[h]} Hundred` : '', rest ? twoDigits(rest) : ''].filter(Boolean).join(' ');
};

export function amountInWords(amount) {
  const value = Math.round(Number(amount || 0) * 100) / 100;
  const rupees = Math.floor(value);
  const paise = Math.round((value - rupees) * 100);
  if (!rupees && !paise) return 'Zero Rupees Only';

  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor(rupees / 100000) % 100;
  const thousand = Math.floor(rupees / 1000) % 100;
  const rest = rupees % 1000;

  const words = [
    crore ? `${threeDigits(crore)} Crore` : '',
    lakh ? `${twoDigits(lakh)} Lakh` : '',
    thousand ? `${twoDigits(thousand)} Thousand` : '',
    rest ? threeDigits(rest) : '',
  ].filter(Boolean).join(' ');

  let out = words ? `${words} Rupees` : '';
  if (paise) out += `${out ? ' and ' : ''}${twoDigits(paise)} Paise`;
  return `${out} Only`;
}

/* ---------- Download ---------- */
export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export const blobToBase64 = (blob) => blobToDataUrl(blob).then((d) => d.split(',')[1]);

/* =============================================================================
   PRINT — isolated hidden iframe
   ============================================================================= */
export async function printInvoiceNode(node, template, title = 'Invoice') {
  if (!node) throw new Error('Invoice is not ready to print yet.');

  const isThermal = Boolean(template?.isThermal);
  const widthMm = getPaperWidthMm(template);

  const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((n) => n.outerHTML)
    .join('\n');

  const baseCss = `
    html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; width: ${widthMm}mm; height: auto !important; }
    body { font-family: '${INVOICE_FONT}', 'Segoe UI', Arial, sans-serif; }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  `;

  const pageCss = isThermal
    ? `@page{size:${widthMm}mm 297mm;margin:0}`
    : `@page{size:A4;margin:${PAGE_MARGIN_MM}mm 0}@page :first{margin-top:0}`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = `position:fixed;left:-10000px;top:0;width:${widthMm}mm;height:1400px;border:0;`;
  document.body.appendChild(iframe);

  const cleanup = () => window.setTimeout(() => iframe.remove(), 300);

  try {
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(
      `<!doctype html><html><head><meta charset="utf-8"/><base href="${document.baseURI}"/>` +
        `<title>${escapeHtml(title)}</title>` +
        `<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin/>` +
        `<link rel="stylesheet" href="${INVOICE_FONT_CSS}"/>` +
        `${styles}<style>${baseCss}</style><style id="page-size">${pageCss}</style>` +
        `</head><body>${node.outerHTML}</body></html>`
    );
    doc.close();

    await new Promise((resolve) => {
      const timer = window.setTimeout(resolve, 2500);
      const done = () => { window.clearTimeout(timer); resolve(); };
      if (doc.readyState === 'complete') done();
      else iframe.onload = done;
    });
    await Promise.all(
      Array.from(doc.images).map((img) =>
        img.complete ? Promise.resolve() : new Promise((r) => { img.onload = r; img.onerror = r; })
      )
    );
    await ensureInvoiceFonts(doc);

    // Thermal: exact roll length, no wasted paper
    if (isThermal) {
      const article = doc.body.firstElementChild;
      const heightMm = Math.ceil(article.getBoundingClientRect().height / MM_TO_PX) + 6;
      doc.getElementById('page-size').textContent = `@page{size:${widthMm}mm ${heightMm}mm;margin:0}`;
    }

    const win = iframe.contentWindow;
    win.onafterprint = cleanup;
    win.focus();
    win.print();
    window.setTimeout(cleanup, 120000);
  } catch (err) {
    cleanup();
    throw err;
  }
}

/* =============================================================================
   PDF
   ============================================================================= */
export async function buildInvoicePdf(node, template) {
  if (!node) throw new Error('Invoice is not ready yet.');

  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);

  const isThermal = Boolean(template?.isThermal);
  const widthPx = getPaperWidthPx(template);
  const widthMm = getPaperWidthMm(template);

  const host = document.createElement('div');
  host.style.cssText = `position:fixed;left:-10000px;top:0;width:${widthPx}px;background:#fff;pointer-events:none;`;
  const clone = node.cloneNode(true);
  Object.assign(clone.style, {
    width: `${widthPx}px`,
    maxWidth: 'none',
    margin: '0',
    boxShadow: 'none',
    transform: 'none',
  });
  host.appendChild(clone);
  document.body.appendChild(host);

  let canvas;
  let breakPoints = [];
  try {
    await Promise.all([ensureInvoiceFonts(document), inlineImages(clone)]);

    const top = clone.getBoundingClientRect().top;
    breakPoints = Array.from(clone.querySelectorAll('tr, section, header'))
      .map((el) => el.getBoundingClientRect().bottom - top)
      .sort((a, b) => a - b);

    canvas = await html2canvas(clone, {
      scale: isThermal ? 3 : 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
      windowWidth: widthPx,
    });
  } finally {
    host.remove();
  }

  const ratio = canvas.width / widthPx;
  const imgHeightMm = (canvas.height * widthMm) / canvas.width;

  if (isThermal) {
    const pageH = Math.max(imgHeightMm, 40);
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [widthMm, pageH], compress: true });
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, widthMm, imgHeightMm);
    return pdf.output('blob');
  }

  const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });

  if (imgHeightMm <= A4_H + 0.5) {
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, A4_W, Math.min(imgHeightMm, A4_H));
    return pdf.output('blob');
  }

  const breaksPx = breakPoints.map((b) => Math.round(b * ratio));
  let y = 0;
  let page = 0;
  while (y < canvas.height - 2) {
    const topMm = page === 0 ? 0 : PAGE_MARGIN_MM;
    const availMm = page === 0 ? A4_H - PAGE_MARGIN_MM : A4_H - PAGE_MARGIN_MM * 2;
    const maxPx = Math.floor((canvas.width * availMm) / A4_W);

    let end = Math.min(y + maxPx, canvas.height);
    if (end < canvas.height) {
      const candidate = breaksPx.filter((b) => b > y + maxPx * 0.5 && b <= end).pop();
      if (candidate) end = candidate;
    }

    const sliceH = end - y;
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = sliceH;
    const ctx = slice.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, y, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', 0, topMm, A4_W, (sliceH * A4_W) / canvas.width);
    y = end;
    page += 1;
  }
  return pdf.output('blob');
}

/* =============================================================================
   SHARE MESSAGE  (WhatsApp supports *bold*)
   ============================================================================= */
// "Mr. Rahul Sharma" -> "Rahul Sharma"; walk-in / draft names -> ''
export const cleanCustomerName = (value) => {
  let name = String(value || '').replace(/\s+/g, ' ').trim();
  if (!name || /walk-?in|draft customer/i.test(name)) return '';
  const title = /^(mr|mrs|ms|miss|mx|dr|shri|shrimati|smt|sri|kumari|km|master)\.?\s+/i;
  while (title.test(name)) name = name.replace(title, '').trim();
  return name;
};

export function buildShareMessage({
  channel, customerName, salonName, salonPhone, invoiceNo, date, total, itemCount, link,
}) {
  const name = cleanCustomerName(customerName);
  const hi = name ? `Hi ${name}` : 'Hi there';
  const amount = formatINR(total);

  if (channel === 'sms') {
    return [
      `${hi}, thank you for visiting ${salonName}!`,
      `Invoice ${invoiceNo} | Amount ${amount}`,
      link ? `View invoice: ${link}` : '',
    ].filter(Boolean).join('\n');
  }

  if (channel === 'email' || channel === 'gmail') {
    return [
      `${hi},`, '',
      `Thank you for visiting ${salonName}. Here are your bill details:`, '',
      `Invoice no.: ${invoiceNo}`,
      date ? `Date: ${date}` : null,
      itemCount ? `Services: ${itemCount}` : null,
      `Amount: ${amount}`, '',
      link ? `View and download your full invoice:\n${link}` : 'Your invoice PDF is attached.', '',
      'Warm regards,', salonName, salonPhone || null,
    ].filter((l) => l !== null).join('\n');
  }

  return [
    `${hi},`,
    '',
    `Thank you for visiting *${salonName}*.`,
    'Here is a summary of your bill:',
    '',
    `Invoice: *${invoiceNo}*`,
    date ? `Date: ${date}` : null,
    itemCount ? `Services: ${itemCount}` : null,
    `Amount: *${amount}*`,
    link ? '' : null,
    link ? '*View and download your full invoice:*' : null,
    link || null,
    '',
    'We look forward to seeing you again.',
    '',
    'Regards,',
    salonName,
    salonPhone ? `Phone: ${salonPhone}` : null,
  ].filter((l) => l !== null).join('\n');
}

/* =============================================================================
   SHARE
   whatsapp     : WhatsApp chat opens with message + link (customer number prefilled)
   whatsapp_api : PDF + message sent directly by the backend (WhatsApp Cloud API)
   sms          : SMS app with message + link
   gmail        : Gmail compose with subject + message + link
   Popup is opened before the first await so browsers don't block it.
   ============================================================================= */
const canShareFiles = () => {
  try {
    if (!isMobileDevice() || !navigator.canShare || !navigator.share) return false;
    return navigator.canShare({ files: [new File(['x'], 'invoice.pdf', { type: 'application/pdf' })] });
  } catch {
    return false;
  }
};

export async function shareInvoice({
  channel, recipient, email, node, template, fileName, subject, getLink, buildMessage, apiSender,
}) {
  const phone = normalizePhone(recipient);
  const needsPopup = (channel === 'whatsapp' && !isMobileDevice()) || channel === 'gmail';
  const popup = needsPopup ? window.open('', '_blank') : null;

  try {
    let link = '';
    if (typeof getLink === 'function') {
      try {
        link = (await getLink()) || '';
      } catch (err) {
        console.warn('Share link unavailable, falling back to PDF:', err);
      }
    }

    const message = buildMessage(channel, link);
    const text = encodeURIComponent(message);
    const pdfName = `${fileName}.pdf`;

    if (channel === 'sms') {
      window.location.href = `sms:${phone}${isIOS() ? '&' : '?'}body=${text}`;
      return { mode: 'sms', link };
    }

    if (channel === 'whatsapp') {
      const waUrl = `https://wa.me/${phone}?text=${text}`;

      if (link) {
        if (popup) popup.location.href = waUrl;
        else window.location.href = waUrl;
        return { mode: 'whatsapp-link', link };
      }

      const blob = await buildInvoicePdf(node, template);
      if (canShareFiles()) {
        try {
          const file = new File([blob], pdfName, { type: 'application/pdf' });
          await navigator.share({ files: [file], title: subject, text: message });
          return { mode: 'native' };
        } catch (err) {
          if (err?.name === 'AbortError') return { mode: 'cancelled' };
        }
      }
      downloadBlob(blob, pdfName);
      if (popup) popup.location.href = waUrl;
      else window.open(waUrl, '_blank');
      return { mode: 'whatsapp-web' };
    }

    if (channel === 'whatsapp_api') {
      if (typeof apiSender !== 'function') throw new Error('WhatsApp API is not configured.');
      if (!phone) throw new Error('No phone number on this bill. Add the customer phone to send via WhatsApp API.');
      const blob = await buildInvoicePdf(node, template);
      const pdfBase64 = await blobToBase64(blob);
      await apiSender({ channel, recipient: phone, fileName: pdfName, message, link, pdfBase64 });
      return { mode: 'api', link };
    }

    if (channel === 'gmail') {
      if (!link) downloadBlob(await buildInvoicePdf(node, template), pdfName);
      const to = isValidEmail(email) ? String(email).trim() : '';
      const gmailUrl =
        `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}` +
        `&su=${encodeURIComponent(subject)}&body=${text}`;
      if (popup) popup.location.href = gmailUrl;
      else window.open(gmailUrl, '_blank');
      return { mode: link ? 'gmail-link' : 'gmail', link };
    }

    if (channel === 'email') {
      if (!link) downloadBlob(await buildInvoicePdf(node, template), pdfName);
      window.location.href =
        `mailto:${encodeURIComponent(String(recipient || '').trim())}?subject=${encodeURIComponent(subject)}&body=${text}`;
      return { mode: link ? 'email-link' : 'email', link };
    }

    throw new Error('Unsupported share channel.');
  } catch (err) {
    if (popup && !popup.closed) popup.close();
    throw err;
  }
}